import { useState, useEffect, useCallback } from "react";
import { sqlReadRaw as sqlRead, payloadOf } from "../../utils/sqlClient";
import {
  optionSetForItem, appQuantity, targetChoices, parseGeom,
} from "../../config/scopeOptions";

const fmt = (n, d = 0) => (n === null || n === undefined || isNaN(n)) ? "—"
  : Number(n).toLocaleString("en-US", { maximumFractionDigits: d, minimumFractionDigits: d });

// Scope & Responsibility Register — the estimating-side master of scope items:
// phase-grouped (6.1 Design / 6.2 Construction / 6.3 Commissioning) activities
// with dynamic party columns and R/A/C/I marks, plus per-item spec capture
// (the intake questions merged from the project development checklist).
// Revision-keyed like the BOD checklist; first open of a revision auto-seeds
// from ScopeItemTemplates with defaults per the project's delivery method.
// After award this register promotes into the turnover Section 6 RACI matrix.

const PHASES = [
  { key: "Design",        num: "6.1" },
  { key: "Construction",  num: "6.2" },
  { key: "Commissioning", num: "6.3" },
];

const MARK_STYLE = {
  R: { background: "#1e3a5f", color: "#fff",    border: "1px solid #1e3a5f" },
  A: { background: "var(--tint-amber)", color: "#92400e", border: "1px solid #fcd34d" },
  C: { background: "#ccfbf1", color: "#0f766e", border: "1px solid #5eead4" },
  I: { background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb" },
};

const MARK_LEGEND = [
  { mark: "R", label: "Responsible", desc: "Does the actual work for the task" },
  { mark: "A", label: "Accountable", desc: "Owns success of the task; decision-maker" },
  { mark: "C", label: "Consulted",   desc: "Consulted for details and requirements" },
  { mark: "I", label: "Informed",    desc: "Kept informed of major updates" },
];

function buildAssignMap(rows) {
  const map = {};
  for (const r of rows) {
    if (!map[r.ScopeItemID]) map[r.ScopeItemID] = {};
    map[r.ScopeItemID][r.ScopePartyID] = r.Mark;
  }
  return map;
}

const getMark = (assigns, itemId, partyId) => assigns?.[itemId]?.[partyId] || null;

export default function ScopeRegister({ projectId }) {
  const [revisions, setRevisions]   = useState([]);
  const [activeRevId, setActiveRevId] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [regLoading, setRegLoading] = useState(false);
  const [header, setHeader]         = useState(null);
  const [parties, setParties]       = useState([]);
  const [items, setItems]           = useState([]);
  const [assigns, setAssigns]       = useState({});
  const [savingId, setSavingId]     = useState(null);
  const [newItem, setNewItem]       = useState({ phase: "Design", discipline: "", activity: "" });
  const [adding, setAdding]         = useState(false);
  const [addingParty, setAddingParty] = useState(false);
  const [filter, setFilter]         = useState("all"); // all | cs | open | intake
  const [rooms, setRooms]           = useState([]);        // from the room takeoff, for derived quantities
  const [applications, setApplications] = useState([]);    // model-based scope placements
  const [expanded, setExpanded]     = useState({});        // ScopeItemID -> interview open
  const [confirmReapply, setConfirmReapply] = useState(false);
  const [reapplying, setReapplying] = useState(false);
  const [reapplyResult, setReapplyResult] = useState(null); // { added, removed } flash
  // Phase E (see project memory): the model interview places a real catalog
  // Item, not a fixed OptionKey. targetableItems is the full pickable
  // catalog (sp_GetScopeTargetableItems); appEntities resolves each existing
  // application's OWN referenced Item, keyed by ScopeApplicationID
  // (sp_GetScopeApplicationEntities) — merged client-side since
  // sp_GetScopeRegister itself can't carry these columns (see
  // db/58_scope_application_items.sql's own header comment on why).
  const [targetableItems, setTargetableItems] = useState([]);
  const [appEntities, setAppEntities] = useState({});

  // Load revisions, default to most recent unlocked (same pattern as BODChecklist)
  useEffect(() => {
    setLoading(true);
    sqlRead(`EXEC sp_GetEstimateRevisions @project_id=${projectId}`)
      .then(result => {
        const revs = result?.Table1 || result?.value || [];
        setRevisions(revs);
        const active = revs.find(r => !r.IsLocked) || revs[0];
        if (active) setActiveRevId(active.RevisionID);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [projectId]);

  const loadRegister = useCallback(() => {
    if (!activeRevId) return;
    setRegLoading(true);
    sqlRead(`EXEC sp_GetScopeRegister @revision_id=${activeRevId}`)
      .then(result => {
        setHeader((result?.Table1 || [])[0] || null);
        setParties(result?.Table2 || []);
        setItems(result?.Table3 || []);
        setAssigns(buildAssignMap(result?.Table4 || []));
        setApplications(result?.Table5 || []);
      })
      .catch(e => { console.error(e); alert("Error loading scope register: " + e.message); })
      .finally(() => setRegLoading(false));

    // Rooms drive the derived quantities on model-based scope placements
    sqlRead(`EXEC sp_GetRoomTakeoff @revision_id=${activeRevId}`)
      .then(result => setRooms(result?.Table3 || []))
      .catch(e => console.error("Room takeoff load failed (placement quantities show as manual):", e));

    // The model interview's item picker (Phase E — see project memory) and
    // each existing application's own resolved entity, merged client-side
    // by ScopeApplicationID since sp_GetScopeRegister can't carry these
    // columns (db/58's own header comment explains why).
    sqlRead("EXEC sp_GetScopeTargetableItems")
      .then(result => setTargetableItems(result?.Table1 || []))
      .catch(e => console.error("Scope targetable items load failed (model interview unavailable):", e));

    sqlRead(`EXEC sp_GetScopeApplicationEntities @revision_id=${activeRevId}`)
      .then(result => {
        const map = {};
        for (const ent of result?.Table1 || []) map[ent.ScopeApplicationID] = ent;
        setAppEntities(map);
      })
      .catch(e => console.error("Scope application entities load failed (model interview shows placeholders):", e));
  }, [activeRevId]);

  useEffect(() => { loadRegister(); }, [loadRegister]);

  const isLocked = !!header?.IsLocked;
  const csParty  = parties.find(p => p.IsCleanSpace);

  const isCsScope = (item) => {
    if (!csParty) return false;
    const m = getMark(assigns, item.ScopeItemID, csParty.ScopePartyID);
    return m === "R" || m === "A";
  };
  const isOpen = (item) => !Object.values(assigns[item.ScopeItemID] || {}).some(Boolean);

  // ── Mark cell: click cycles blank → R → A → C → I (optimistic) ───────────────
  async function reapplyScopeDelivery() {
    setReapplying(true);
    try {
      const payload = payloadOf({ revision_id: activeRevId });
      const result = await sqlRead(`EXEC sp_ReapplyScopeDeliveryMethod @payload = '${payload}'`);
      const row = (result?.Table1 || result?.value || [])[0] || {};
      setReapplyResult({ added: row.MarksAdded ?? 0, removed: row.MarksRemoved ?? 0 });
      setTimeout(() => setReapplyResult(null), 4000);
      setConfirmReapply(false);
      loadRegister();
    } catch (e) {
      alert("Error re-applying delivery method: " + e.message);
    } finally {
      setReapplying(false);
    }
  }

  async function cycleMark(item, party) {
    if (isLocked) return;
    const cycle = [null, "R", "A", "C", "I"];
    const cur = getMark(assigns, item.ScopeItemID, party.ScopePartyID);
    const next = cycle[(cycle.indexOf(cur) + 1) % cycle.length];
    const prev = assigns;
    setAssigns(m => {
      const row = { ...(m[item.ScopeItemID] || {}) };
      if (next) row[party.ScopePartyID] = next;
      else delete row[party.ScopePartyID];
      return { ...m, [item.ScopeItemID]: row };
    });
    try {
      const payload = payloadOf({
        revision_id:    activeRevId,
        scope_item_id:  item.ScopeItemID,
        scope_party_id: party.ScopePartyID,
        mark:           next,
      });
      await sqlRead(`EXEC sp_SaveScopeMark @payload = '${payload}'`);
    } catch (e) {
      setAssigns(prev);
      alert("Error saving mark: " + e.message);
    }
  }

  async function saveItem(item) {
    if (isLocked) return;
    setSavingId(item.ScopeItemID);
    try {
      const payload = payloadOf({
        scope_item_id: item.ScopeItemID,
        revision_id:   activeRevId,
        phase:         item.Phase,
        discipline:    item.Discipline || null,
        activity:      item.Activity,
        spec_answer:   item.SpecAnswer || null,
        notes:         item.Notes || null,
        scope_level:   item.ScopeLevel || null,
      });
      await sqlRead(`EXEC sp_SaveScopeItem @payload = '${payload}'`);
      setItems(prev => prev.map(i => i.ScopeItemID === item.ScopeItemID ? item : i));
    } catch (e) { alert("Error saving item: " + e.message); }
    finally { setSavingId(null); }
  }

  async function deleteItem(item) {
    if (isLocked) return;
    if (!window.confirm(`Remove "${item.Activity}" from this revision's scope register?`)) return;
    try {
      const payload = payloadOf({ scope_item_id: item.ScopeItemID, revision_id: activeRevId, is_delete: 1 });
      await sqlRead(`EXEC sp_SaveScopeItem @payload = '${payload}'`);
      setItems(prev => prev.filter(i => i.ScopeItemID !== item.ScopeItemID));
    } catch (e) { alert("Error: " + e.message); }
  }

  // ── Model-based scope placements (the "interview") ───────────────────────────
  const appsByItem = applications.reduce((m, a) => {
    (m[a.ScopeItemID] ||= []).push(a);
    return m;
  }, {});

  // Defaults to the first entry in the real catalog (Phase E — see project
  // memory) rather than a fixed OptionKey. Two calls, not one:
  // sp_SaveScopeApplication (db/25) creates the row's other fields;
  // sp_SaveScopeApplicationEntity (db/58) links it to the catalog Item —
  // that second proc had to live in a later file than db/25, see its own
  // header comment on the ordering hazard that forced this split.
  async function addApplication(item) {
    if (isLocked) return;
    const firstItem = targetableItems[0];
    if (!firstItem) { alert("No placeable catalog items configured yet — add one in Setup > Estimate Engine."); return; }
    const existing = appsByItem[item.ScopeItemID] || [];
    const order = (existing[existing.length - 1]?.DisplayOrder || 90) + 10;
    try {
      const payload = payloadOf({
        revision_id:   activeRevId,
        scope_item_id: item.ScopeItemID,
        unit:          firstItem.UOM,
        target_type:   "all_rooms",
        target_key:    null,
        display_order: order,
      });
      const res = await sqlRead(`EXEC sp_SaveScopeApplication @payload = '${payload}'`);
      const newId = (res?.Table1 || [])[0]?.ScopeApplicationID;
      const entityPayload = payloadOf({ scope_application_id: newId, kind: "item", item_id: firstItem.ItemID });
      await sqlRead(`EXEC sp_SaveScopeApplicationEntity @payload = '${entityPayload}'`);
      setApplications(prev => [...prev, {
        ScopeApplicationID: newId, RevisionID: activeRevId, ScopeItemID: item.ScopeItemID,
        Kind: "item", ItemID: firstItem.ItemID, AssemblyID: null,
        Unit: firstItem.UOM, TargetType: "all_rooms", TargetKey: null,
        QuantityOverride: null, Notes: null, DisplayOrder: order,
      }]);
      setAppEntities(prev => ({ ...prev, [newId]: firstItem }));
    } catch (e) { alert("Error adding placement: " + e.message); }
  }

  // Switches which catalog Item an existing placement references — the
  // model interview's own option picker, post-Phase E.
  async function saveApplicationEntity(app, item) {
    if (isLocked) return;
    const prevApps = applications, prevEntities = appEntities;
    setApplications(list => list.map(a => a.ScopeApplicationID === app.ScopeApplicationID
      ? { ...a, Kind: "item", ItemID: item.ItemID, AssemblyID: null, Unit: item.UOM } : a));
    setAppEntities(prev => ({ ...prev, [app.ScopeApplicationID]: item }));
    try {
      const payload = payloadOf({ scope_application_id: app.ScopeApplicationID, kind: "item", item_id: item.ItemID, unit: item.UOM });
      await sqlRead(`EXEC sp_SaveScopeApplicationEntity @payload = '${payload}'`);
    } catch (e) {
      setApplications(prevApps); setAppEntities(prevEntities);
      alert("Error updating placement item: " + e.message);
    }
  }

  async function saveApplication(app, patch) {
    if (isLocked) return;
    const merged = { ...app, ...patch };
    const prev = applications;
    setApplications(list => list.map(a => a.ScopeApplicationID === app.ScopeApplicationID ? merged : a));
    try {
      const payload = payloadOf({
        scope_application_id: app.ScopeApplicationID,
        revision_id:      activeRevId,
        scope_item_id:    merged.ScopeItemID,
        option_key:       merged.OptionKey,
        unit:             merged.Unit || null,
        target_type:      merged.TargetType,
        target_key:       merged.TargetKey ?? null,
        quantity_override: merged.QuantityOverride ?? null,
        notes:            merged.Notes || null,
        display_order:    merged.DisplayOrder ?? 100,
      });
      await sqlRead(`EXEC sp_SaveScopeApplication @payload = '${payload}'`);
    } catch (e) { setApplications(prev); alert("Error saving placement: " + e.message); }
  }

  async function deleteApplication(app) {
    if (isLocked) return;
    setApplications(prev => prev.filter(a => a.ScopeApplicationID !== app.ScopeApplicationID));
    try {
      const payload = payloadOf({ scope_application_id: app.ScopeApplicationID, revision_id: activeRevId, is_delete: 1 });
      await sqlRead(`EXEC sp_SaveScopeApplication @payload = '${payload}'`);
    } catch (e) { alert("Error removing placement: " + e.message); loadRegister(); }
  }

  async function addCustomItem() {
    if (isLocked || !newItem.activity.trim()) return;
    setAdding(true);
    try {
      const payload = payloadOf({
        revision_id:   activeRevId,
        phase:         newItem.phase,
        discipline:    newItem.discipline.trim() || null,
        activity:      newItem.activity.trim(),
        is_custom:     1,
        display_order: 9000,
      });
      await sqlRead(`EXEC sp_SaveScopeItem @payload = '${payload}'`);
      setNewItem({ phase: newItem.phase, discipline: "", activity: "" });
      loadRegister();
    } catch (e) { alert("Error: " + e.message); }
    finally { setAdding(false); }
  }

  async function addParty() {
    if (isLocked) return;
    const name = window.prompt("Party name (firm or person):");
    if (!name?.trim()) return;
    setAddingParty(true);
    try {
      const payload = payloadOf({ revision_id: activeRevId, party_name: name.trim() });
      await sqlRead(`EXEC sp_SaveScopeParty @payload = '${payload}'`);
      loadRegister();
    } catch (e) { alert("Error adding party: " + e.message); }
    finally { setAddingParty(false); }
  }

  async function renameParty(party, name) {
    if (isLocked) return;
    try {
      const payload = payloadOf({ scope_party_id: party.ScopePartyID, revision_id: activeRevId, party_name: name });
      await sqlRead(`EXEC sp_SaveScopeParty @payload = '${payload}'`);
      setParties(prev => prev.map(p => p.ScopePartyID === party.ScopePartyID ? { ...p, PartyName: name } : p));
    } catch (e) { alert("Error saving party name: " + e.message); }
  }

  async function deleteParty(party) {
    if (isLocked) return;
    if (!window.confirm(`Remove "${party.PartyName}" and all of its R/A/C/I marks?`)) return;
    try {
      const payload = payloadOf({ scope_party_id: party.ScopePartyID, revision_id: activeRevId, is_delete: 1 });
      await sqlRead(`EXEC sp_SaveScopeParty @payload = '${payload}'`);
      loadRegister();
    } catch (e) { alert("Error removing party: " + e.message); }
  }

  // ── Derived ──────────────────────────────────────────────────────────────────
  const scopeCount = items.filter(isCsScope).length;
  const openCount  = items.filter(isOpen).length;
  const visible = items.filter(i =>
    filter === "cs"     ? isCsScope(i)
    : filter === "open"   ? isOpen(i)
    : filter === "intake" ? i.IsIntakeOnly
    : true
  );
  const phaseDefs = [
    ...PHASES.filter(p => visible.some(i => i.Phase === p.key)),
    ...[...new Set(visible.map(i => i.Phase))].filter(ph => !PHASES.some(p => p.key === ph)).map(ph => ({ key: ph, num: null })),
  ];
  const anchorId = (key) => `scope-sub-${key.replace(/\W+/g, "-").toLowerCase()}`;
  const gridCols = `minmax(260px, 1fr) repeat(${parties.length}, 60px) minmax(140px, 200px) 34px`;
  const gridMinWidth = 500 + parties.length * 60;

  const cellInput = {
    width: "100%", padding: "4px 8px", fontSize: 12,
    border: "1px solid var(--cs-border)", borderRadius: 5,
    background: "var(--surface)", color: "var(--text)",
  };
  const chipBtn = (on) => ({
    padding: "4px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer",
    background: on ? "var(--cs-blue)" : "var(--surface)",
    color: on ? "#fff" : "var(--text2)",
    border: `1px solid ${on ? "var(--cs-blue)" : "var(--cs-border)"}`, borderRadius: 999,
  });

  // The model-based scope "interview" for one placeable register item.
  // `optionSetForItem()` still gates whether this item shows the interview
  // at all (its keyword match against Activity, unchanged) — the material
  // picked once it's open is a real catalog Item (Phase E, see project
  // memory), resolved per-application via appEntities.
  function renderInterview(item) {
    const set = optionSetForItem(item);
    if (!set) return null;
    const apps = appsByItem[item.ScopeItemID] || [];
    const choices = targetChoices(rooms);

    // Rollup by item for the header summary
    const rollup = {};
    for (const a of apps) {
      const ent = appEntities[a.ScopeApplicationID];
      if (!ent) continue;
      const { qty } = appQuantity(ent, a, rooms);
      const key = ent.ItemID ?? ent.AssemblyID;
      rollup[key] ||= { label: ent.Name, unit: ent.UOM, qty: 0 };
      rollup[key].qty += +qty || 0;
    }
    const rollupText = Object.values(rollup).filter(r => r.qty > 0)
      .map(r => `${r.label}: ${fmt(r.qty)} ${r.unit}`).join("  ·  ");

    const appGrid = "minmax(150px,1fr) minmax(150px,1.2fr) 150px minmax(120px,1fr) 30px";
    const ctl = { fontSize: 11.5, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "4px 6px", background: "var(--surface)", color: "var(--text)", width: "100%" };

    return (
      <div style={{ padding: "10px 14px 14px 30px", background: "#f7faff", borderBottom: "1px solid var(--cs-border)" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11.5, fontWeight: 800, color: "var(--cs-blue)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            🧩 {set.label} — placements
          </span>
          {rollupText && <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text2)", fontVariantNumeric: "tabular-nums" }}>{rollupText}</span>}
        </div>

        {apps.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: appGrid, gap: 6, alignItems: "center", fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
            <div>Type</div><div>Where</div><div>Quantity</div><div>Notes</div><div />
          </div>
        )}

        {apps.map(a => {
          const ent = appEntities[a.ScopeApplicationID];
          const isDrawn = a.TargetType === "drawn";
          const { qty: derived, derived: isDerived } = appQuantity(ent, a, rooms);
          const manual = !isDerived && a.QuantityOverride == null; // needs a typed value
          const targetVal = `${a.TargetType}::${a.TargetKey ?? ""}`;
          const geom = isDrawn ? parseGeom(a) : null;
          return (
            <div key={a.ScopeApplicationID} style={{ display: "grid", gridTemplateColumns: appGrid, gap: 6, alignItems: "center", marginBottom: 6 }}>
              <select
                value={a.ItemID ?? ""}
                disabled={isLocked}
                onChange={e => {
                  const picked = targetableItems.find(t => t.ItemID === +e.target.value);
                  if (picked) saveApplicationEntity(a, picked);
                }}
                style={ctl}
              >
                {targetableItems.map(t => <option key={t.ItemID} value={t.ItemID}>{t.Name}</option>)}
              </select>
              {isDrawn ? (
                <span title="Drawn on the Layout Canvas" style={{ ...ctl, display: "inline-flex", alignItems: "center", gap: 5, background: "#eef2ff", color: "#4338ca", fontWeight: 600 }}>
                  ✒️ Drawn run{geom?.lengthFt ? ` · ${fmt(geom.lengthFt)} ft` : ""}
                </span>
              ) : (
                <select
                  value={targetVal}
                  disabled={isLocked}
                  onChange={e => { const [tt, tk] = e.target.value.split("::"); saveApplication(a, { TargetType: tt, TargetKey: tk || null }); }}
                  style={ctl}
                >
                  <optgroup label="Location">
                    {choices.quick.map(c => <option key={`${c.targetType}::${c.targetKey ?? ""}`} value={`${c.targetType}::${c.targetKey ?? ""}`}>{c.label}</option>)}
                  </optgroup>
                  {choices.rooms.length > 0 && (
                    <optgroup label="Specific room">
                      {choices.rooms.map(c => <option key={`room::${c.targetKey}`} value={`room::${c.targetKey}`}>{c.label}</option>)}
                    </optgroup>
                  )}
                </select>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input
                  type="number"
                  min={0}
                  disabled={isLocked}
                  defaultValue={a.QuantityOverride ?? ""}
                  placeholder={manual ? "enter" : fmt(derived)}
                  onBlur={e => { const v = e.target.value === "" ? null : +e.target.value; if (v !== (a.QuantityOverride ?? null)) saveApplication(a, { QuantityOverride: v }); }}
                  title={manual ? "Enter the quantity" : `Auto: ${fmt(derived)} ${ent?.UOM}. Type to override.`}
                  style={{ ...ctl, textAlign: "right", width: 78 }}
                  key={`${a.ScopeApplicationID}-${a.TargetType}-${a.TargetKey}-${a.ItemID}-${geom?.lengthFt ?? ""}`}
                />
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", minWidth: 18 }}>{ent?.UOM}</span>
                {isDerived && a.QuantityOverride == null && (
                  <span title="Derived from the geometry" style={{ fontSize: 9, fontWeight: 800, color: "#166534", background: "var(--tint-green)", borderRadius: 3, padding: "1px 4px" }}>auto</span>
                )}
              </div>
              <input
                defaultValue={a.Notes || ""}
                disabled={isLocked}
                placeholder="—"
                onBlur={e => { if ((e.target.value || null) !== (a.Notes || null)) saveApplication(a, { Notes: e.target.value || null }); }}
                style={ctl}
              />
              <button
                onClick={() => deleteApplication(a)}
                disabled={isLocked}
                title="Remove this placement"
                style={{ background: "none", border: "none", cursor: isLocked ? "default" : "pointer", fontSize: 12, color: "#d1d5db", padding: 2 }}
                onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
                onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}
              >🗑</button>
            </div>
          );
        })}

        {!isLocked && (
          <button
            onClick={() => addApplication(item)}
            style={{ marginTop: 4, padding: "4px 12px", fontSize: 11.5, fontWeight: 700, background: "var(--surface)", color: "var(--cs-blue)", border: "1px dashed var(--cs-blue)", borderRadius: 6, cursor: "pointer" }}
          >
            + Add placement
          </button>
        )}
        {rooms.length === 0 && (
          <div style={{ marginTop: 6, fontSize: 11, color: "#d97706" }}>
            Add rooms in Room Takeoff to auto-calculate quantities — until then, enter them manually.
          </div>
        )}
      </div>
    );
  }

  if (loading) return <div style={{ color: "var(--text3)", fontSize: 13 }}>Loading scope register…</div>;

  if (revisions.length === 0) return (
    <div style={{ maxWidth: 560 }}>
      <div className="panel-title">Scope &amp; RACI</div>
      <div className="panel-sub">No revisions yet. Create a revision from the Revisions tab to start the scope register.</div>
    </div>
  );

  return (
    <div style={{ maxWidth: "none" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="panel-title">Scope &amp; RACI</div>
          <div className="panel-sub">
            One register for scope, responsibility, and intake capture — merged from the development
            checklist and responsibility matrix. Defaults seed from{" "}
            {header?.ScopeDeliveryMethod
              ? <><strong>{header.ScopeDeliveryMethod}</strong> scope type</>
              : <>the project&rsquo;s delivery method ({header?.DeliveryMethod || "Design-Build"})</>
            }; click a cell to cycle R / A / C / I.
            CleanSpace R or A rows are the scope that flows to the proposal and, after award, the turnover.
          </div>
          {header?.ScopeDeliveryMethod && !isLocked && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 100,
                background: header.ScopeDeliveryMethod === "Box" ? "var(--tint-blue)" : header.ScopeDeliveryMethod === "Box+" ? "var(--tint-green)" : "#faf5ff",
                color:      header.ScopeDeliveryMethod === "Box" ? "#2563eb" : header.ScopeDeliveryMethod === "Box+" ? "#15803d" : "#7e22ce",
                border: `1px solid ${header.ScopeDeliveryMethod === "Box" ? "#bfdbfe" : header.ScopeDeliveryMethod === "Box+" ? "#bbf7d0" : "#e9d5ff"}`,
              }}>{header.ScopeDeliveryMethod}</span>
              {reapplyResult ? (
                <span style={{ fontSize: 12, color: "#15803d" }}>
                  ✓ {reapplyResult.added} R-marks added, {reapplyResult.removed} removed
                </span>
              ) : confirmReapply ? (
                <>
                  <span style={{ fontSize: 12, color: "var(--text3)" }}>
                    Reset CleanSpace R-marks to {header.ScopeDeliveryMethod} defaults?
                  </span>
                  <button
                    onClick={reapplyScopeDelivery}
                    disabled={reapplying}
                    style={{ fontSize: 12, padding: "2px 10px", borderRadius: "var(--radius)", border: "none", background: "#1e3a5f", color: "#fff", cursor: "pointer" }}
                  >{reapplying ? "Applying…" : "Confirm"}</button>
                  <button
                    onClick={() => setConfirmReapply(false)}
                    style={{ fontSize: 12, padding: "2px 10px", borderRadius: "var(--radius)", border: "1px solid var(--cs-border)", background: "var(--surface)", color: "var(--text)", cursor: "pointer" }}
                  >Cancel</button>
                </>
              ) : (
                <button
                  onClick={() => setConfirmReapply(true)}
                  style={{ fontSize: 12, padding: "2px 10px", borderRadius: "var(--radius)", border: "1px solid var(--cs-border)", background: "var(--surface)", color: "var(--text)", cursor: "pointer" }}
                >Re-apply defaults</button>
              )}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "var(--text3)" }}>Revision:</span>
          <select
            value={activeRevId || ""}
            onChange={e => setActiveRevId(+e.target.value)}
            style={{ height: 30, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 8px", fontSize: 12, color: "var(--text)", background: "var(--surface)", outline: "none" }}
          >
            {revisions.map(r => (
              <option key={r.RevisionID} value={r.RevisionID}>
                Rev {r.RevisionNumber}: {r.RevisionLabel}{r.IsLocked ? " 🔒" : ""}
              </option>
            ))}
          </select>
          {isLocked && (
            <span style={{ fontSize: 11, color: "#d97706", fontWeight: 600, padding: "2px 8px", borderRadius: 100, background: "var(--tint-amber)" }}>
              Locked — create new revision to edit
            </span>
          )}
        </div>
      </div>

      {regLoading ? (
        <div style={{ color: "var(--text3)", fontSize: 13 }}>Loading register…</div>
      ) : (
        <>
          {/* Summary + filters + legend */}
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={() => setFilter("all")} style={chipBtn(filter === "all")}>All ({items.length})</button>
            <button onClick={() => setFilter("cs")} style={chipBtn(filter === "cs")}>CleanSpace scope ({scopeCount})</button>
            <button onClick={() => setFilter("open")} style={chipBtn(filter === "open")}>Unassigned ({openCount})</button>
            <button onClick={() => setFilter("intake")} style={chipBtn(filter === "intake")}>Intake questions ({items.filter(i => i.IsIntakeOnly).length})</button>
            <span style={{ flex: 1 }} />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {MARK_LEGEND.map(({ mark, label, desc }) => (
                <span key={mark} title={desc} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--text3)" }}>
                  <span style={{ ...MARK_STYLE[mark], borderRadius: 4, fontSize: 10, fontWeight: 800, padding: "1px 6px" }}>{mark}</span>
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* Phase jump + add party */}
          <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
            {phaseDefs.map(p => (
              <button
                key={p.key}
                onClick={() => document.getElementById(anchorId(p.key))?.scrollIntoView({ behavior: "smooth", block: "start" })}
                style={{ ...chipBtn(false), color: "var(--cs-blue)" }}
              >
                {p.num ? `${p.num} ${p.key}` : p.key}
              </button>
            ))}
            <span style={{ flex: 1 }} />
            {!isLocked && (
              <button
                onClick={addParty}
                disabled={addingParty}
                title="Add a party column (a firm or person responsible for work on this estimate)"
                style={{ ...chipBtn(false), border: "1px dashed var(--cs-border)", opacity: addingParty ? 0.5 : 1 }}
              >
                {addingParty ? "Adding…" : "+ Add Party"}
              </button>
            )}
          </div>

          <div style={{ border: "1px solid var(--cs-border)", borderRadius: 8, overflowX: "auto" }}>
            <div style={{ minWidth: gridMinWidth }}>
              {/* Column header */}
              <div style={{ display: "grid", gridTemplateColumns: gridCols, background: "var(--surface2)", borderBottom: "1px solid var(--cs-border)" }}>
                <div style={{ padding: "8px 14px", fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Activity / Question
                </div>
                {parties.map(p => (
                  <div key={p.ScopePartyID} style={{ padding: "4px 2px", position: "relative" }}>
                    <input
                      defaultValue={p.PartyName}
                      disabled={isLocked}
                      onBlur={e => {
                        const v = e.target.value.trim();
                        if (v && v !== p.PartyName) renameParty(p, v);
                        else e.target.value = p.PartyName;
                      }}
                      title={`${p.PartyName}${p.IsCleanSpace ? " (CleanSpace — anchors scope tracking)" : ""} — click to rename`}
                      style={{
                        width: "100%", border: "none", background: "transparent",
                        fontSize: 10, fontWeight: 700,
                        color: p.IsCleanSpace ? "var(--cs-blue)" : "var(--text3)",
                        textAlign: "center", textTransform: "uppercase", letterSpacing: "0.02em",
                        outline: "none", fontFamily: "inherit",
                      }}
                    />
                    {!p.IsCleanSpace && !isLocked && (
                      <button
                        onClick={() => deleteParty(p)}
                        title={`Remove ${p.PartyName} and its marks`}
                        style={{ position: "absolute", top: -2, right: 0, background: "none", border: "none", cursor: "pointer", fontSize: 10, color: "#d1d5db", padding: 2, lineHeight: 1 }}
                        onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
                        onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <div style={{ padding: "8px 14px", fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Notes
                </div>
                <div />
              </div>

              {phaseDefs.map(p => {
                const phaseItems = visible.filter(i => i.Phase === p.key);
                let lastDiscipline = null;
                return (
                  <div key={p.key} id={anchorId(p.key)} style={{ scrollMarginTop: 12 }}>
                    <div style={{ padding: "7px 14px", background: "#1e3a5f", fontSize: 11, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      {p.num ? `${p.num} — ${p.key}` : p.key}
                    </div>
                    {phaseItems.map((item, i) => {
                      const showDiscipline = item.Discipline && item.Discipline !== lastDiscipline;
                      lastDiscipline = item.Discipline || lastDiscipline;
                      const scope = isCsScope(item);
                      const open = isOpen(item);
                      return (
                        <div key={item.ScopeItemID}>
                          {showDiscipline && (
                            <div style={{ padding: "5px 14px", background: "#eef2f7", fontSize: 10.5, fontWeight: 700, color: "var(--text2)", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid var(--cs-border)" }}>
                              {item.Discipline}
                            </div>
                          )}
                          <div style={{
                            display: "grid", gridTemplateColumns: gridCols, alignItems: "center",
                            borderBottom: i < phaseItems.length - 1 ? "1px solid var(--cs-border)" : "none",
                            background: scope ? "var(--tint-blue)" : open ? "var(--tint-amber)" : i % 2 === 0 ? "transparent" : "var(--surface2)",
                            borderLeft: scope ? "3px solid var(--cs-blue)" : open ? "3px solid #f3d689" : "3px solid transparent",
                            opacity: savingId === item.ScopeItemID ? 0.55 : 1,
                          }}>
                            <div style={{ padding: "7px 14px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                {item.IsCustom ? (
                                  <input
                                    defaultValue={item.Activity}
                                    disabled={isLocked}
                                    onBlur={e => { if (e.target.value.trim() && e.target.value !== item.Activity) saveItem({ ...item, Activity: e.target.value }); }}
                                    style={{ flex: 1, border: "none", background: "transparent", fontSize: 12.5, color: "var(--text)", fontFamily: "inherit", outline: "none", fontStyle: "italic" }}
                                  />
                                ) : (
                                  <span style={{ fontSize: 12.5, color: "var(--text)" }}>{item.Activity}</span>
                                )}
                                {!!item.IsIntakeOnly && (
                                  <span title="Intake question — captured for the estimate and clarifications; not printed on the proposal RACI pages" style={{ fontSize: 9, fontWeight: 800, color: "#5b5f9e", background: "#e9eaf7", borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                    intake
                                  </span>
                                )}
                                {!item.IsIntakeOnly && (
                                  <button
                                    disabled={isLocked}
                                    onClick={() => saveItem({ ...item, ScopeLevel: item.ScopeLevel === "room" ? "project" : "room" })}
                                    title={item.ScopeLevel === "room"
                                      ? "Room-level scope — appears in each room's drill-down on the Room Takeoff (click to make project-level)"
                                      : "Project-level scope — applies once per project (click to make room-level)"}
                                    style={{
                                      fontSize: 9, fontWeight: 800, borderRadius: 4, padding: "1px 5px",
                                      whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.04em",
                                      border: "none", cursor: isLocked ? "default" : "pointer",
                                      color: item.ScopeLevel === "room" ? "#166534" : "#6b7280",
                                      background: item.ScopeLevel === "room" ? "var(--tint-green)" : "#f3f4f6",
                                    }}
                                  >
                                    {item.ScopeLevel === "room" ? "room" : "project"}
                                  </button>
                                )}
                                {(() => {
                                  const set = optionSetForItem(item);
                                  if (!set) return null;
                                  const n = (appsByItem[item.ScopeItemID] || []).length;
                                  const on = !!expanded[item.ScopeItemID];
                                  return (
                                    <button
                                      onClick={() => setExpanded(e => ({ ...e, [item.ScopeItemID]: !e[item.ScopeItemID] }))}
                                      title="Model-based scope — place this by location and type; quantities derive from the rooms"
                                      style={{ fontSize: 9, fontWeight: 800, borderRadius: 4, padding: "1px 6px", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.04em", border: "none", cursor: "pointer", color: on ? "#fff" : "#5b21b6", background: on ? "#5b21b6" : "#f5f3ff" }}
                                    >
                                      🧩 {n > 0 ? `model · ${n}` : "model"}
                                    </button>
                                  );
                                })()}
                              </div>
                              {item.SpecPrompt && (
                                <div style={{ marginTop: 4 }}>
                                  <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 2 }}>{item.SpecPrompt}</div>
                                  <input
                                    defaultValue={item.SpecAnswer || ""}
                                    disabled={isLocked}
                                    onBlur={e => { if ((e.target.value || null) !== (item.SpecAnswer || null)) saveItem({ ...item, SpecAnswer: e.target.value || null }); }}
                                    placeholder="Capture answer…"
                                    style={{ ...cellInput, fontStyle: item.SpecAnswer ? "normal" : "italic" }}
                                  />
                                </div>
                              )}
                            </div>
                            {parties.map(pt => {
                              const mark = getMark(assigns, item.ScopeItemID, pt.ScopePartyID);
                              return (
                                <div key={pt.ScopePartyID} style={{ padding: "5px 3px", textAlign: "center" }}>
                                  <button
                                    onClick={() => cycleMark(item, pt)}
                                    disabled={isLocked}
                                    title={`${pt.PartyName} — click to cycle blank → R → A → C → I`}
                                    style={{
                                      width: 32, height: 25, borderRadius: 5, cursor: isLocked ? "default" : "pointer",
                                      fontSize: 12, fontWeight: 800, fontFamily: "inherit",
                                      ...(mark ? MARK_STYLE[mark] : { background: "var(--surface)", color: "#d1d5db", border: "1px dashed var(--cs-border)" }),
                                    }}
                                  >
                                    {mark || "·"}
                                  </button>
                                </div>
                              );
                            })}
                            <div style={{ padding: "5px 10px" }}>
                              <input
                                defaultValue={item.Notes || ""}
                                disabled={isLocked}
                                onBlur={e => { if ((e.target.value || null) !== (item.Notes || null)) saveItem({ ...item, Notes: e.target.value || null }); }}
                                placeholder="—"
                                title={item.Notes || ""}
                                style={cellInput}
                              />
                            </div>
                            <div style={{ padding: "5px 4px", textAlign: "center" }}>
                              {!isLocked && (
                                <button
                                  onClick={() => deleteItem(item)}
                                  title="Remove this item from this revision"
                                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#d1d5db", padding: "2px 4px" }}
                                  onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
                                  onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}
                                >
                                  🗑
                                </button>
                              )}
                            </div>
                          </div>
                          {expanded[item.ScopeItemID] && renderInterview(item)}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Add custom item */}
          {!isLocked && (
            <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
              <select
                value={newItem.phase}
                onChange={e => setNewItem({ ...newItem, phase: e.target.value })}
                style={{ padding: "6px 8px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }}
              >
                {PHASES.map(p => <option key={p.key} value={p.key}>{p.num} — {p.key}</option>)}
              </select>
              <input
                value={newItem.discipline}
                onChange={e => setNewItem({ ...newItem, discipline: e.target.value })}
                placeholder="Discipline (optional)"
                style={{ width: 180, padding: "6px 10px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }}
              />
              <input
                value={newItem.activity}
                onChange={e => setNewItem({ ...newItem, activity: e.target.value })}
                onKeyDown={e => e.key === "Enter" && addCustomItem()}
                placeholder="Add activity / scope item…"
                style={{ flex: "1 1 220px", padding: "6px 10px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }}
              />
              <button
                onClick={addCustomItem}
                disabled={adding || !newItem.activity.trim()}
                style={{
                  padding: "6px 16px", fontSize: 13, fontWeight: 700,
                  background: "var(--cs-blue)", color: "#fff",
                  border: "none", borderRadius: 6, cursor: "pointer",
                  opacity: (adding || !newItem.activity.trim()) ? 0.5 : 1,
                  whiteSpace: "nowrap",
                }}
              >
                {adding ? "Adding…" : "+ Add"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
